import { useState } from "react";
import { dropshippingInfluencers } from "@/mocks/dropshipping";

const badgeColors: Record<string, string> = {
  "Top Earner": "bg-amber-500 text-white",
  "Viral Seller": "bg-rose-500 text-white",
  "Rising Star": "bg-violet-500 text-white",
  "Consistent": "bg-emerald-500 text-white",
  "Deal Hunter": "bg-sky-500 text-white",
  "Steady Growth": "bg-stone-500 text-white",
};

const platformMeta: Record<string, { icon: string; color: string; bg: string }> = {
  YouTube: { icon: "ri-youtube-fill", color: "text-red-500", bg: "bg-red-50" },
  TikTok: { icon: "ri-tiktok-fill", color: "text-gray-900", bg: "bg-gray-100" },
  Facebook: { icon: "ri-facebook-circle-fill", color: "text-sky-500", bg: "bg-sky-50" },
  Twitter: { icon: "ri-twitter-x-fill", color: "text-gray-900", bg: "bg-gray-100" },
};

const platforms = ["All", "YouTube", "TikTok", "Facebook", "Twitter"];

export default function DropshippingInfluencers() {
  const [activePlatform, setActivePlatform] = useState("All");

  const filtered = activePlatform === "All"
    ? dropshippingInfluencers
    : dropshippingInfluencers.filter((inf) => inf.platform === activePlatform);

  const totalSold = dropshippingInfluencers.reduce((a, i) => a + i.productsSold, 0);
  const totalCommission = dropshippingInfluencers.reduce((a, i) => {
    return a + parseFloat(i.commissionEarned.replace("$", "").replace(",", ""));
  }, 0);

  const top = [...dropshippingInfluencers].sort((a, b) => b.productsSold - a.productsSold)[0];

  return (
    <div className="mt-10">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Earn While You Promote</span>
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-1">
            Social Stars Commission Board
          </h3>
          <p className="text-gray-500 text-sm mt-1">
            Facebook · TikTok · Twitter · YouTube stars promote products and earn real commissions
          </p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="font-bold text-gray-900 text-lg">{totalSold.toLocaleString()}</div>
            <div className="text-stone-400 text-xs">Total Products Sold</div>
          </div>
          <div className="text-right">
            <div className="font-bold text-amber-600 text-lg">${totalCommission.toLocaleString()}</div>
            <div className="text-stone-400 text-xs">Total Commission Paid</div>
          </div>
        </div>
      </div>

      {/* Top Performer Banner */}
      {top && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-center gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full overflow-hidden ring-4 ring-white/40 flex-shrink-0">
            <img src={top.avatar} alt={top.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
              <i className="ri-trophy-line text-white text-lg"></i>
              <span className="text-white/80 text-xs font-semibold uppercase tracking-wider">Top Performer This Month</span>
            </div>
            <h4 className="text-white font-bold text-lg">{top.name}</h4>
            <p className="text-white/80 text-sm">{top.specialty} · {top.followers} followers on {top.platform}</p>
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <div className="text-white font-bold text-2xl">{top.productsSold.toLocaleString()}</div>
              <div className="text-white/70 text-xs">Products Sold</div>
            </div>
            <div>
              <div className="text-white font-bold text-2xl">{top.commissionEarned}</div>
              <div className="text-white/70 text-xs">Commission Earned</div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        {platforms.map((p) => {
          const meta = platformMeta[p];
          return (
            <button
              key={p}
              onClick={() => setActivePlatform(p)}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activePlatform === p
                  ? "bg-amber-500 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {meta && <i className={`${meta.icon} text-sm`}></i>}
              {p}
            </button>
          );
        })}
      </div>

      {/* Influencer Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((inf, idx) => {
          const meta = platformMeta[inf.platform];
          const commissionNum = parseFloat(inf.commissionEarned.replace("$", "").replace(",", ""));
          const maxCommission = 35000;
          const pct = Math.min((commissionNum / maxCommission) * 100, 100);

          return (
            <div
              key={inf.id}
              className="bg-white rounded-2xl border border-stone-100 p-5 hover:border-amber-200 transition-all duration-200"
            >
              {/* Rank + Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-stone-100 text-stone-500 font-bold text-xs">
                    #{idx + 1}
                  </div>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full ${meta?.bg || "bg-gray-100"}`}>
                    <i className={`${meta?.icon || "ri-global-line"} ${meta?.color || "text-gray-600"} text-sm`}></i>
                  </div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${badgeColors[inf.badge] || "bg-stone-100 text-stone-600"}`}>
                  {inf.badge}
                </span>
              </div>

              {/* Avatar + Info */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0">
                  <img src={inf.avatar} alt={inf.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm">{inf.name}</div>
                  <div className="text-stone-400 text-xs">{inf.specialty}</div>
                  <div className="text-stone-400 text-xs">{inf.followers} followers</div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-stone-50 rounded-xl p-3 text-center">
                  <div className="font-bold text-gray-900 text-base">{inf.productsSold.toLocaleString()}</div>
                  <div className="text-stone-400 text-xs">Products Sold</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <div className="font-bold text-amber-600 text-base">{inf.commissionEarned}</div>
                  <div className="text-stone-400 text-xs">Commission Earned</div>
                </div>
              </div>

              {/* Commission Bar */}
              <div className="mb-3">
                <div className="flex justify-between text-xs text-stone-400 mb-1">
                  <span>Commission Progress</span>
                  <span>{inf.conversionRate} conv.</span>
                </div>
                <div className="bg-stone-100 rounded-full h-1.5">
                  <div
                    className="bg-amber-400 h-1.5 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>

              {/* Top Product */}
              <div className="flex items-center gap-2 bg-stone-50 rounded-lg px-3 py-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-star-line text-amber-500 text-xs"></i>
                </div>
                <span className="text-xs text-stone-500 truncate">Top: {inf.topProduct}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA to become influencer */}
      <div className="mt-6 bg-gray-950 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h4 className="text-white font-bold text-base mb-1">Are you a Social Media Star?</h4>
          <p className="text-gray-400 text-sm">
            Join Crown Dropshipping as an influencer — promote products, earn up to 20% commission on every sale.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {["ri-youtube-fill", "ri-tiktok-fill", "ri-facebook-circle-fill", "ri-twitter-x-fill"].map((icon) => (
            <div key={icon} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
              <i className={`${icon} text-white text-sm`}></i>
            </div>
          ))}
          <button className="ml-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm">
            Apply as Influencer
          </button>
        </div>
      </div>
    </div>
  );
}
